"use client";

import { FC, useMemo } from "react";
import { AssetsDirectory, DocumentStatus, TranslationScopes } from "@finbackoffice/enums";
import {
    useProfileDetailsConfig,
    useRuntimeConfig,
    useUserDocuments,
} from "@finbackoffice/site-core";
import { useRouter } from "next/navigation";
import { RouteNames } from "utils/constants";
import Translate from "components/base/translate/Translate";
import Button from "components/base/button/Button";
import { Svg } from "components/base/svg/Svg";
import { usePersonalDetailsVerificationConfig } from "hooks/base/usePersonalDetailsVerificationConfig";
import ChangePasswordForm from "../change-password/ChangePasswordForm";
import UserInfoForm from "./UserInfoForm";
import styles from "./user-details.module.sass";

type IProps = {
    onVerificationClick?: () => void;
};
export const UserDetails: FC<IProps> = ({ onVerificationClick }) => {
    const router = useRouter();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { accountDetailsConfigCollections } = useProfileDetailsConfig();
    const { needToVerify } = usePersonalDetailsVerificationConfig();
    const { docStatus } = useUserDocuments();

    const showWarningIcon = useMemo(
        () =>
            needToVerify ||
            Object.values(docStatus).some((status) => status === DocumentStatus.REJECTED),
        [needToVerify, docStatus],
    );

    return (
        <>
            {accountDetailsConfigCollections?.map((config) => (
                <UserInfoForm key={config.name} config={config} />
            ))}
            {COMMON_SITE_CONFIGS.verification.enable && (
                <Button
                    type="button"
                    variant="custom"
                    className={styles.verificationBtn}
                    onClick={() => {
                        if (onVerificationClick) {
                            onVerificationClick();
                        } else {
                            router.push(
                                `/${RouteNames.ACCOUNT}/${RouteNames.DETAILS}/${RouteNames.PERSONAL}/${RouteNames.VERIFICATION}`,
                            );
                        }
                    }}>
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-identity_document.svg`}
                        className="svg-icon"
                    />
                    <span>
                        <Translate
                            tid="account_details_verification_heading"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </span>
                    {showWarningIcon && (
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-warning.svg`}
                            className={styles.warningIcon}
                        />
                    )}
                </Button>
            )}
            <ChangePasswordForm />
            <Translate
                tid="account_details_personal_infoPersonal"
                dangerous
                namespace={TranslationScopes.UserAccount}>
                <div className={styles.personalDetailsTxt} />
            </Translate>
        </>
    );
};
